import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO Configuration for each page
const seoConfig = {
  '/': {
    title: 'Ritiro e Riciclo Cavi Elettrici Torino | FIRM s.a.s.',
    description: 'Ritiro e riciclo cavi elettrici in Canavese, Torino e provincia. Recupero rame al 99%. Servizio per elettricisti, installatori e imprese.',
    keywords: 'ritiro cavi elettrici Canavese, ritiro cavi elettrici Torino, smaltimento cavi elettrici, recupero cavi elettrici, riciclo cavi elettrici, dove portare cavi elettrici, quotazione cavi elettrici',
  },
  '/chi-siamo': {
    title: 'Chi Siamo - Esperti in Riciclo Cavi Elettrici | FIRM s.a.s.',
    description: 'FIRM s.a.s. è specializzata in cavi elettrici, non rottamai generici. Operiamo in Canavese, Torino e Piemonte con quotazione trasparente.',
    keywords: 'specializzati cavi elettrici, esperti cavi, solo cavi elettrici, non rottamai generici, recupero materiali cavi, Canavese, Piemonte',
  },
  '/servizi': {
    title: 'Ritiro Cavi Elettrici per Professionisti | FIRM s.a.s.',
    description: 'Ritiro cavi elettrici per elettricisti, installatori e imprese edili. Smaltimento cavi cantiere e industriali. Torino e provincia.',
    keywords: 'servizio ritiro cavi, ritiro cavi cantiere, smaltimento cavi industriali, cavi elettrici da smaltire, recupero cavi elettrici, quotazione trasparente cavi',
  },
  '/processo': {
    title: 'Riciclo Cavi Elettrici - Recupero Rame 99% | FIRM',
    description: 'Processo di riciclo cavi elettrici con recupero rame certificato al 99% e riciclo polimeri di qualità. Dal cavo alla risorsa.',
    keywords: 'riciclo cavi elettrici, recupero rame, riciclo polimeri, processo recupero cavi, quanto pagano cavi elettrici, come smaltire cavi di scarto',
  },
  '/sostenibilita': {
    title: 'Sostenibilità e Economia Circolare | FIRM s.a.s.',
    description: 'Il nostro impegno per l\'economia circolare: riciclo cavi elettrici, recupero materiali e riduzione dell\'impatto ambientale in Piemonte.',
    keywords: 'economia circolare, riciclo cavi elettrici, recupero materiali cavi, sostenibilità ambientale, Piemonte',
  },
  '/professionisti': {
    title: 'Ritiro Cavi per Elettricisti e Installatori | FIRM',
    description: 'Servizio dedicato per elettricisti, installatori e imprese. Ritiro cavi elettrici in Canavese, Torino e provincia. Quotazione trasparente.',
    keywords: 'ritiro cavi elettrici per elettricisti, installatori elettrici, imprese edili, cantieri, professionisti settore elettrico, impiantisti, smaltimento cavi fine cantiere, chi ritira cavi elettrici Piemonte',
  },
  '/contatti': {
    title: 'Contatti - Ritiro Cavi Elettrici Torino | FIRM s.a.s.',
    description: 'Contattaci per ritiro cavi elettrici in Canavese, Torino e zone limitrofe. Dove smaltire cavi elettrici vicino Torino? Quotazione gratuita.',
    keywords: 'dove smaltire cavi elettrici vicino Torino, chi ritira cavi elettrici Piemonte, contatti ritiro cavi, Canavese, provincia di Torino, Canavese e zone limitrofe',
  },
  '/privacy': {
    title: 'Privacy Policy | FIRM s.a.s.',
    description: 'Informativa sul trattamento dei dati personali di FIRM s.a.s.',
    keywords: 'privacy policy, trattamento dati personali, GDPR, FIRM s.a.s.',
  },
};

const SITE_URL = 'https://firmsas.it';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_copper-recovery/artifacts/u6wvh72q_F.I.R.M.png';

// Structured Data for Local Business
const structuredData = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "FIRM s.a.s.",
  "description": "Specializzati nel ritiro e riciclo di cavi elettrici in Canavese, Torino e Piemonte. Recupero rame certificato al 99% e polimeri di qualità per l'economia circolare.",
  "url": SITE_URL,
  "logo": LOGO_URL,
  "image": LOGO_URL,
  "telephone": "+390124572040",
  "email": "firm.recupero@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "addressRegion": "Piemonte",
    "addressLocality": "Canavese",
    "addressCountry": "IT"
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "Canavese"
    },
    {
      "@type": "Place", 
      "name": "Torino"
    },
    {
      "@type": "Place",
      "name": "Provincia di Torino"
    },
    {
      "@type": "Place",
      "name": "Piemonte"
    }
  ],
  "serviceType": [
    "Ritiro cavi elettrici",
    "Riciclo cavi elettrici",
    "Smaltimento cavi elettrici",
    "Recupero rame",
    "Riciclo polimeri"
  ],
  "priceRange": "€€",
  "openingHours": "Mo-Fr 09:00-12:30, 14:00-18:00",
  "sameAs": []
};

export const SEOHead = () => {
  const location = useLocation();
  const path = location.pathname;
  const seo = seoConfig[path] || seoConfig['/'];

  useEffect(() => {
    // Update title
    document.title = seo.title;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seo.description);
    }

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', seo.keywords);

    // Update or create Open Graph tags
    const pageUrl = SITE_URL + path;
    const ogTags = {
      'og:title': seo.title,
      'og:description': seo.description,
      'og:type': 'website',
      'og:locale': 'it_IT',
      'og:site_name': 'FIRM s.a.s.',
      'og:url': pageUrl,
      'og:image': LOGO_URL,
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Update or create Twitter Card tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': seo.title,
      'twitter:description': seo.description,
      'twitter:image': LOGO_URL,
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    // Add structured data
    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

    // Add canonical URL (always pointing to production domain)
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = SITE_URL + path;

    // Add hreflang tag for Italian targeting
    let hreflang = document.querySelector('link[hreflang="it"]');
    if (!hreflang) {
      hreflang = document.createElement('link');
      hreflang.rel = 'alternate';
      hreflang.hreflang = 'it';
      document.head.appendChild(hreflang);
    }
    hreflang.href = SITE_URL + path;

  }, [path, seo]);

  return null;
};
